// dashboard/Paiements.jsx
import React, { useState, useEffect } from 'react';
import { Search, Download, BarChart3, CheckCircle, XCircle, Clock, RefreshCcw } from 'lucide-react';
import './Paiements.css';

const Paiements = () => {
    const [payments, setPayments] = useState([]);
    const [filteredPayments, setFilteredPayments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('tous');
    const [categoryFilter, setCategoryFilter] = useState('toutes');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        total: 0,
        valides: 0,
        enAttente: 0
    });

    // Données d'exemple basées sur l'image
    useEffect(() => {
        const mockPayments = [
            {
                id: 1,
                societe: 'Techno Solutions CI',
                reference: 'FM-222345',
                date: '01/07/2024',
                montant: '50000 FCFA',
                categorie: 'Immatriculation',
                statut: 'Payé'
            },
            {
                id: 2,
                societe: 'Agri Business Côte d\'Ivoire',
                reference: '-',
                date: '06/07/2024',
                montant: '70000 FCFA',
                categorie: 'Immatriculation',
                statut: 'En Attente'
            },
            {
                id: 3,
                societe: 'Immobilier Premium',
                reference: 'FM-222340',
                date: '04/07/2024',
                montant: '50000 FCFA',
                categorie: 'Modification',
                statut: 'Payé'
            },
            {
                id: 4,
                societe: 'Transport Express',
                reference: '-',
                date: '02/07/2024',
                montant: '50000 FCFA',
                categorie: 'Radiation',
                statut: 'Remboursé'
            },
            {
                id: 5,
                societe: 'Eco Energie',
                reference: 'FM-222335',
                date: '28/06/2024',
                montant: '50000 FCFA',
                categorie: 'Modification',
                statut: 'Payé'
            },
            {
                id: 6,
                societe: 'Cabinet Juridique Associés',
                reference: 'FM-222342',
                date: '05/07/2024',
                montant: '65000 FCFA',
                categorie: 'Immatriculation',
                statut: 'Payé'
            },
            {
                id: 7,
                societe: 'Santé Plus',
                reference: 'FM-222330',
                date: '25/06/2024',
                montant: '50000 FCFA',
                categorie: 'Radiation',
                statut: 'Payé'
            },
            {
                id: 8,
                societe: 'Banque Atlantique CI',
                reference: 'FM-222325',
                date: '20/06/2024',
                montant: '75000 FCFA',
                categorie: 'Modification',
                statut: 'Payé'
            }
        ];

        setTimeout(() => {
            setPayments(mockPayments);
            setFilteredPayments(mockPayments);

            // Calculer les stats
            const totalAmount = mockPayments.reduce((sum, payment) => {
                return sum + parseInt(payment.montant.replace(/[^\d]/g, ''));
            }, 0);

            setStats({
                total: totalAmount,
                valides: mockPayments.filter(p => p.statut === 'Payé').length,
                enAttente: mockPayments.filter(p => p.statut === 'En Attente').length
            });

            setLoading(false);
        }, 1000);
    }, []);

    // Filtrage
    useEffect(() => {
        let filtered = payments;

        if (searchTerm) {
            filtered = filtered.filter(payment =>
                payment.societe.toLowerCase().includes(searchTerm.toLowerCase()) ||
                payment.reference.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (statusFilter !== 'tous') {
            filtered = filtered.filter(payment => {
                switch (statusFilter) {
                    case 'payes': return payment.statut === 'Payé';
                    case 'en-attente': return payment.statut === 'En Attente';
                    case 'rembourses': return payment.statut === 'Remboursé';
                    case 'rejetes': return payment.statut === 'Rejeté';
                    default: return true;
                }
            });
        }

        if (categoryFilter !== 'toutes') {
            filtered = filtered.filter(payment =>
                payment.categorie.toLowerCase() === categoryFilter.toLowerCase()
            );
        }

        setFilteredPayments(filtered);
    }, [payments, searchTerm, statusFilter, categoryFilter]);

    const getStatusIcon = (statut) => {
        switch (statut) {
            case 'Payé': return <CheckCircle size={16} className="text-green-600" />;
            case 'En Attente': return <Clock size={16} className="text-orange-600" />;
            case 'Remboursé': return <RefreshCcw size={16} className="text-blue-600" />;
            case 'Rejeté': return <XCircle size={16} className="text-red-600" />;
            default: return null;
        }
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Chargement des paiements...</p>
            </div>
        );
    }

    return (
        <div className="paiements-container">
            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-content">
                        <h3>Total des paiements</h3>
                        <p className="stat-value text-green-600">{stats.total.toLocaleString()} FCFA</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-content">
                        <h3>Paiements validés</h3>
                        <p className="stat-value text-blue-600">{stats.valides}</p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-content">
                        <h3>Paiements en attente</h3>
                        <p className="stat-value text-orange-600">{stats.enAttente}</p>
                    </div>
                </div>
            </div>

            {/* Header avec filtres */}
            <div className="paiements-header">
                <div className="search-container">
                    <Search size={20} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Rechercher un paiement..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="filters-container">
                    <button className="filter-button">
                        <Download size={16} />
                        Exporter
                    </button>
                    <button className="filter-button">
                        <BarChart3 size={16} />
                        Statistiques
                    </button>

                    <div className="status-filter">
                        <Clock size={16} />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="tous">Tous les statuts</option>
                            <option value="payes">Payés</option>
                            <option value="en-attente">En attente</option>
                            <option value="rembourses">Remboursés</option>
                            <option value="rejetes">Rejetés</option>
                        </select>
                    </div>

                    <div className="category-filter">
                        <span>Catégorie:</span>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="toutes">Toutes les catégories</option>
                            <option value="immatriculation">Immatriculation</option>
                            <option value="modification">Modification</option>
                            <option value="radiation">Radiation</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Onglets de filtrage */}
            <div className="tabs-container">
                <button
                    className={`tab ${statusFilter === 'tous' ? 'tab-active' : ''}`}
                    onClick={() => setStatusFilter('tous')}
                >
                    Tous
                </button>
                <button
                    className={`tab ${statusFilter === 'payes' ? 'tab-active' : ''}`}
                    onClick={() => setStatusFilter('payes')}
                >
                    Payés
                </button>
                <button
                    className={`tab ${statusFilter === 'en-attente' ? 'tab-active' : ''}`}
                    onClick={() => setStatusFilter('en-attente')}
                >
                    En attente
                </button>
                <button
                    className={`tab ${statusFilter === 'rembourses' ? 'tab-active' : ''}`}
                    onClick={() => setStatusFilter('rembourses')}
                >
                    Remboursés
                </button>
                <button
                    className={`tab ${statusFilter === 'rejetes' ? 'tab-active' : ''}`}
                    onClick={() => setStatusFilter('rejetes')}
                >
                    Rejetés
                </button>
            </div>

            {/* Table */}
            <div className="table-container">
                <table className="payments-table">
                    <thead>
                        <tr>
                            <th>Société</th>
                            <th>Référence</th>
                            <th>Date</th>
                            <th>Montant</th>
                            <th>Catégorie</th>
                            <th>Statut</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredPayments.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="no-data">
                                    {searchTerm ? 'Aucun paiement trouvé' : 'Aucun paiement'}
                                </td>
                            </tr>
                        ) : (
                            filteredPayments.map((payment) => (
                                <tr key={payment.id}>
                                    <td className="company-name">{payment.societe}</td>
                                    <td className="reference">{payment.reference}</td>
                                    <td>{payment.date}</td>
                                    <td className="amount">{payment.montant}</td>
                                    <td>{payment.categorie}</td>
                                    <td>
                                        <span className={`status-badge status-${payment.statut.toLowerCase().replace(' ', '-')}`}>
                                            {getStatusIcon(payment.statut)}
                                            {payment.statut}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="actions-menu">
                                            <button className="action-icon" title="Voir détails">
                                                👁️
                                            </button>
                                            {payment.statut === 'En Attente' && (
                                                <>
                                                    <button className="action-icon" title="Valider">
                                                        ✅
                                                    </button>
                                                    <button className="action-icon" title="Rejeter">
                                                        ❌
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Paiements;